"use client";

import React from "react";
import {getUtmParams, isCrmActive, isEmail} from "utils";
import CloseIcon from '@mui/icons-material/Close';
import {Slide, TextField, Button, Dialog, Grid, LinearProgress, useMediaQuery, Alert, IconButton} from "@mui/material"
import {styled} from '@mui/material/styles';
import brandLogo from "../../assets/img/poln_logo_teal.svg";
import ParseHtml from "../ParseHtml";
import {useTheme} from '@mui/material/styles';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import {useSearchParams} from "next/navigation";
import {useConfig} from "../ConfigProvider";

const StyledTextField = styled(TextField)(({theme}) => ({
    width: '100%'
}));

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

function Header({title, logo = brandLogo, success, htmlSuccessTitleHeading}) {
    return (
        <div style={{display: 'flex', justifyContent: 'space-between', flexDirection: 'column', paddingBottom: '0px'}}>
            {
                success ?
                    <div>
                        {/*
                            This component get html form hygraph and render it in React
                        */}
                        {/*<ParseHtml text={htmlSuccessTitleHeading} />*/}
                        <h2 style={{marginTop: '10px'}} className="text-center waitlist-modal-title">{title}</h2>
                    </div>
                    :
                    <h2 className="text-center waitlist-modal-title">{title}</h2>
            }
        </div>
    );
}

function Success(props) {
    const {onClose, title = "Our team will be in touch shortly", message} = props;

    return <div>
        <div style={{margin: 16}}>
            <Header title={title}/>
            {message}
        </div>
        <div className="d-flex justify-content-center">
            <div className="rounded-pill theme-btn red-color mt-5" onClick={onClose}>Done</div>
        </div>
    </div>
}

function Hubspot(props) {
    const {onClose, firstname, lastname, email, hubSpotMeeting} = props;
    const [meetingComplete, setMeetingComplete] = React.useState(false)

    const url = React.useMemo(() => {
        if (!hubSpotMeeting) return ""; // Handle empty meeting URL safely

        const baseUrl = hubSpotMeeting.endsWith("?") ? hubSpotMeeting : `${hubSpotMeeting}?`;
        const urlObj = new URL(baseUrl, window.location.origin); // Ensures proper URL parsing

        if (firstname) {
            urlObj.searchParams.append("firstname", firstname);
        }
        if (lastname) {
            urlObj.searchParams.append("lastname", lastname);
        }
        if (email) {
            urlObj.searchParams.append("email", email);
        }

        return urlObj.toString();
    }, [firstname, lastname, email, hubSpotMeeting]);

    window.addEventListener("message", function(event){
        // Ensure the message is coming from the correct origin
        if (event.origin !== "https://meetings.hubspot.com") {
            return;
        }
        // Check if the meeting was successfully booked
        if (event.data.meetingBookSucceeded) {
            // Trigger your desired action here
            console.log('Meeting successfully scheduled!');
            setMeetingComplete(true);
        }
    })
    return <div>
        <div style={{margin: 16}}>
            <iframe
                id='hs-meeting'
                src={url}
                width="100%"
                height="600px"
                style={{border: "none"}}
            />
        </div>
        {meetingComplete && <div className="d-flex justify-content-center">
            <div className="rounded-pill theme-btn red-color" onClick={onClose}>Close</div>
        </div>}
    </div>
}

function Intro(props) {
    const {introMessage, introAction, setIntroComplete} = props;

    return (<div>
        <div style={{margin: 16}}>
            <ParseHtml message={introMessage}/>
        </div>
        <div className="d-flex justify-content-center">
            <div className="rounded-pill theme-btn red-color mt-5"
                 onClick={() => setIntroComplete(true)}>{introAction}</div>
        </div>
    </div>)
}

export default function ContactForm(props) {
    const {
        onClose, id = "business-contact", open, title, message, subMessage, fields,
        persona = "Publisher", successMessage, successTitle, actionText = "Get In Touch",
        logo, hubSpotMeeting, introMessage, introAction
    } = props;

    const searchParams = useSearchParams()
    const theme = useTheme();
    const [firstname, setFirstname] = React.useState("")
    const [lastname, setLastname] = React.useState("")
    const [website, setWebsite] = React.useState("")
    const [company, setCompany] = React.useState("")
    const [socialChannel, setSocialChannel] = React.useState("")
    const [postingRate, setPostingRate] = React.useState("")
    const [email, setEmail] = React.useState("")
    const [phone, setPhone] = React.useState("")
    const [jobTitle, setJobTitle] = React.useState("")
    const [emailError, setEmailError] = React.useState("")
    const [success, setSuccess] = React.useState(false)
    const [progress, setProgress] = React.useState(false);
    const [updateError, setUpdateError] = React.useState(null);
    const [introComplete, setIntroComplete] = React.useState(false);
    const config = useConfig()

    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const {showForm, showIntro, showSuccess, showHubspot} =
        React.useMemo(() => {
            const hasIntro = introMessage;
            let showForm = !success && (!hasIntro || introComplete)
            let showSuccess = success && (!hasIntro || introComplete) && !hubSpotMeeting
            let showIntro = !success && hasIntro && !introComplete
            let showHubspot = success && hubSpotMeeting

            return {showForm, showSuccess, showIntro, showHubspot}
        }, [success, introMessage, hubSpotMeeting, introComplete, updateError]);

    function handleUpdate(func, value) {
        setUpdateError(null)
        return func(value);
    }

    function handleReset() {
        setFirstname("")
        setLastname("")
        setCompany("")
        setEmail("")
        setPhone("")
        setJobTitle("")
    }

    function handleForm() {
        if (!isEmail(email)) {
            setEmailError("You must provide a valid email");
            return;
        }

        async function createContact() {
            try {
                setProgress(true)

                try {
                    const utmParams = getUtmParams(searchParams);
                    if (isCrmActive() && config) {
                        const response = await fetch(`${config.siteApi.apiUrl}/crm/createContact`, {
                            method: 'POST',
                            body: JSON.stringify({
                                firstname, lastname, email, website, company, phone, jobTitle, persona,
                                postingRate, socialChanel, utmParams
                            })
                        })

                        const d = await response.json();
                        if (!response.ok) {
                            console.error(d, response.status);
                            setUpdateError(response.status);
                        }
                    }

                    setSuccess(true);

                } catch (err) {
                    setUpdateError(err.message);
                }
            } finally {
                setProgress(false);

                if (success) {
                    handleReset();//reset on success
                }
            }
        }

        setUpdateError(null);
        return createContact();
    }

    function showField(field) {
        if (!fields) return true;

        return fields.includes(field)
    }

    return (
        <>
            <Dialog TransitionComponent={Transition}
                    disableScrollLock={true}
                    onClose={onClose} open={Boolean(open)}
                    fullScreen={Boolean(fullScreen)}
                    maxWidth={'lg'}>
                <IconButton
                    onClick={onClose}
                    style={{position: 'absolute', top: 8, right: 8, zIndex: 1000}}
                    aria-label="close"
                >
                    <CloseIcon/>
                </IconButton>
                <div style={{margin: 16}}>
                    <Header title={title} logo={logo} success={success}/>
                    {showForm && <>
                        <p style={{marginBottom: 8}}>
                            {message}
                        </p>
                        <p style={{paddingTop: 16}}>
                            {subMessage}
                        </p>
                    </>
                    }
                    {showIntro &&
                        <Intro introMessage={introMessage} introAction={introAction}
                               setIntroComplete={setIntroComplete}/>
                    }
                    {progress && <LinearProgress/>}
                    {updateError && <Alert className="red" severity="error">
                        {updateError}
                    </Alert>
                    }
                    {showForm &&
                        <Grid container spacing={2} padding={2}>
                            {showField("company") &&
                                <Grid item xs={12}>
                                    <StyledTextField value={company}
                                                     onChange={(e) => handleUpdate(setCompany, e.target.value)}
                                                     label={"Company"} variant={"outlined"} className="form-element"/>
                                </Grid>}
                            {showField("website") &&
                                <Grid item xs={12}>
                                    <StyledTextField value={website}
                                                     onChange={(e) => handleUpdate(setWebsite, e.target.value)}
                                                     label={"Company Website"} variant={"outlined"}/>
                                </Grid>
                            }
                            {showField("socialChannel") &&
                                <Grid item xs={12}>
                                    <FormControl fullWidth variant="outlined">
                                        <InputLabel id="social-channel-label">
                                            Where is your largest social media audience
                                        </InputLabel>
                                        <Select
                                            label="Where is your largest social media audience"
                                            labelId="social-channel-label"
                                            id="social-channel"
                                            value={socialChannel}
                                            onChange={(e) => handleUpdate(setSocialChannel, e.target.value)}>
                                            <MenuItem value={"instagram"}>Instagram</MenuItem>
                                            <MenuItem value={"tiktok"}>Tik Tok</MenuItem>
                                            <MenuItem value={"youtube"}>You Tube</MenuItem>
                                            <MenuItem value={"x"}>Twitter/X</MenuItem>
                                            <MenuItem value={"pinterest"}>Pinterest</MenuItem>
                                            <MenuItem value={"blog"}>Blog</MenuItem>
                                            <MenuItem value={"other"}>Other</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            }
                            {showField("postingRate") &&
                                <Grid item xs={12}>
                                    <FormControl fullWidth variant="outlined">
                                        <InputLabel id="social-channel-label">
                                            How often do you post to your primary channel?
                                        </InputLabel>
                                        <Select
                                            label="Where is your largest social media audience"
                                            labelId="social-channel-label"
                                            id="social-channel"
                                            value={postingRate}
                                            onChange={(e) => handleUpdate(setPostingRate, e.target.value)}>
                                            <MenuItem value={"multi-per-day"}>Multiple times per day</MenuItem>
                                            <MenuItem value={"daily"}>Daily</MenuItem>
                                            <MenuItem value={"weekly"}>Weekly</MenuItem>
                                            <MenuItem value={"monthly"}>Monthly</MenuItem>
                                            <MenuItem value={"few-months"}>Every few months</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            }
                            {showField("firstname") &&
                                <Grid item xs={12} md={6}>
                                    <StyledTextField label="First Name" value={firstname}
                                                     onChange={(e) => handleUpdate(setFirstname, e.target.value)}
                                                     variant="outlined"/>
                                </Grid>
                            }
                            {showField("lastname") &&
                                <Grid item xs={12} md={6}>
                                    <StyledTextField label="Last Name" value={lastname}
                                                     onChange={(e) => handleUpdate(setLastname, e.target.value)}
                                                     variant="outlined"/>
                                </Grid>
                            }
                            {showField("email") &&
                                <Grid item xs={12}>
                                    <StyledTextField label="Email"
                                                     error={Boolean(emailError)}
                                                     helperText={emailError}
                                                     value={email}
                                                     onChange={(e) => {
                                                         setEmailError(null);
                                                         setUpdateError(null);
                                                         setEmail(e.target.value)
                                                     }}
                                                     variant="outlined"/>
                                </Grid>
                            }
                            {showField("phone") &&
                                <Grid item xs={12}>
                                    <StyledTextField label="Phone" value={phone}
                                                     onChange={(e) => handleUpdate(setPhone, e.target.value)}
                                                     variant="outlined"/>
                                </Grid>
                            }
                            {showField("jobTitle") &&
                                <Grid item xs={12}>
                                    <StyledTextField label="Job Title" value={jobTitle}
                                                     onChange={(e) => handleUpdate(setJobTitle, e.target.value)}
                                                     variant="outlined"/>
                                </Grid>
                            }
                            <Grid item xs={12} fullWidth>
                                <small className="p-2 text-muted">By providing your information you agree to our <a
                                    href="/page/privacy">privacy
                                    policy</a>
                                </small>
                            </Grid>
                            <Grid item xs={12}>
                                <Button id={id} disabled={progress} className="theme-btn red-color" variant="contained"
                                        onClick={handleForm}>{actionText}</Button>
                            </Grid>
                        </Grid>
                    }
                    {showSuccess &&
                        <Success message={successMessage} title={successTitle} open={Boolean(success)} onClose={() => {
                            setSuccess(false)
                            onClose();
                        }}/>
                    }
                    {showHubspot &&
                        <Hubspot
                            hubSpotMeeting={hubSpotMeeting}
                                 firstname={firstname}
                                 lastname={lastname}
                                 email={email}
                                 onClose={onClose}
                        />
                    }
                </div>
            </Dialog>

        </>
    )

}
